import React, { useState, useRef, useCallback } from 'react'
import {
  Palette,
  Upload,
  Image,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Loader2
} from 'lucide-react'

// --- Types ---

export interface BrandingFormState {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  textColor: string
  backgroundColor: string
  logoUrl: string | null
}

interface BrandingEditorProps {
  branding: BrandingFormState
  onChange: (branding: BrandingFormState) => void
  onLogoUpload: (file: File) => Promise<void>
  onLogoRemove: () => Promise<void>
  uploadingLogo: boolean
  disabled?: boolean
}

// --- Color Slot Configuration ---

const COLOR_SLOTS = [
  { key: 'primaryColor' as const, label: 'Primary Color', description: 'Main brand color, buttons, links', default: '#2563eb' },
  { key: 'secondaryColor' as const, label: 'Secondary Color', description: 'Secondary buttons, accents', default: '#f59e0b' },
  { key: 'accentColor' as const, label: 'Accent Color', description: 'Highlights, hover states', default: '#10b981' },
  { key: 'textColor' as const, label: 'Text Color', description: 'Primary text color', default: '#1f2937' },
  { key: 'backgroundColor' as const, label: 'Background Color', description: 'Widget background', default: '#ffffff' },
]

export const DEFAULT_BRANDING: BrandingFormState = {
  primaryColor: '#2563eb',
  secondaryColor: '#f59e0b',
  accentColor: '#10b981',
  textColor: '#1f2937',
  backgroundColor: '#ffffff',
  logoUrl: null,
}

// --- WCAG Contrast Utilities ---

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return [0, 0, 0]
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

function contrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(...hexToRgb(hex1))
  const l2 = relativeLuminance(...hexToRgb(hex2))
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function getContrastWarning(textColor: string, bgColor: string): string | null {
  const ratio = contrastRatio(textColor, bgColor)
  if (ratio < 3.0) return `Very low contrast (${ratio.toFixed(1)}:1). Text may be unreadable.`
  if (ratio < 4.5) return `Low contrast (${ratio.toFixed(1)}:1). Does not meet WCAG AA for normal text.`
  return null
}

// --- Accepted Logo Types ---

const LOGO_ACCEPTED_TYPES = '.png,.jpg,.jpeg,.webp'
const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024 // 2MB
const LOGO_ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp']

// --- Component ---

const BrandingEditor: React.FC<BrandingEditorProps> = ({
  branding,
  onChange,
  onLogoUpload,
  onLogoRemove,
  uploadingLogo,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const handleColorChange = useCallback((key: keyof BrandingFormState, value: string) => {
    onChange({ ...branding, [key]: value })
  }, [branding, onChange])

  const handleFileSelect = useCallback(async (file: File) => {
    setFileError(null)
    // Client-side type check
    if (!LOGO_ALLOWED_MIMES.includes(file.type)) {
      setFileError('Only PNG, JPEG, and WebP images are allowed')
      return
    }
    // Client-side size check
    if (file.size > LOGO_MAX_SIZE_BYTES) {
      setFileError('File must be smaller than 2 MB')
      return
    }
    await onLogoUpload(file)
  }, [onLogoUpload])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    // Reset input so re-uploading the same file triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleFileSelect])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled || uploadingLogo) return
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [disabled, uploadingLogo, handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleResetToDefaults = useCallback(() => {
    // Reset colors but preserve logoUrl
    onChange({
      ...DEFAULT_BRANDING,
      logoUrl: branding.logoUrl,
    })
  }, [branding.logoUrl, onChange])

  const contrastWarning = getContrastWarning(branding.textColor, branding.backgroundColor)

  return (
    <div className="space-y-6">
      {/* Brand Colors Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Brand Colors</h2>
        </div>

        <div className="space-y-3">
          {COLOR_SLOTS.map((slot) => (
            <div
              key={slot.key}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-1 min-w-0 mr-3">
                <p className="text-sm font-medium text-gray-700">{slot.label}</p>
                <p className="text-xs text-gray-500">{slot.description}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-xs font-mono text-gray-500 uppercase">
                  {branding[slot.key]}
                </span>
                <input
                  type="color"
                  value={branding[slot.key]}
                  onChange={(e) => handleColorChange(slot.key, e.target.value)}
                  disabled={disabled}
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          ))}
        </div>

        {/* WCAG Contrast Warning */}
        {contrastWarning && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">{contrastWarning}</p>
          </div>
        )}

        {/* Reset to Defaults */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleResetToDefaults}
            disabled={disabled}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Logo Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Image className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Logo</h2>
        </div>

        {branding.logoUrl ? (
          /* Logo exists -- show preview and remove button */
          <div className="flex items-center gap-4">
            <img
              src={branding.logoUrl}
              alt="Operator logo"
              className="w-16 h-16 rounded-lg object-cover border border-gray-200"
            />
            <div className="flex-1">
              <p className="text-sm text-gray-700">Current logo</p>
              <button
                onClick={onLogoRemove}
                disabled={disabled || uploadingLogo}
                className="mt-1 inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
                Remove Logo
              </button>
            </div>
          </div>
        ) : (
          /* No logo -- show upload area */
          <div
            onClick={() => !disabled && !uploadingLogo && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              disabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
            }`}
          >
            {uploadingLogo ? (
              <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-2 animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            )}
            <p className="text-sm text-gray-600">
              {uploadingLogo ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPEG, or WebP up to 2MB</p>
            {fileError && (
              <p className="text-sm text-red-600 mt-2">{fileError}</p>
            )}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={LOGO_ACCEPTED_TYPES}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>
    </div>
  )
}

export default BrandingEditor
